import React, {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from './utils/classname';

export type PopoverPosition = 'top' | 'bottom' | 'left' | 'right';

export interface PopoverProps extends PropsWithChildren {
  trigger: ReactNode;
  children: ReactNode;
  position?: PopoverPosition;
  showArrow?: boolean;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  contentClassName?: string;
  arrowClassName?: string;
  offset?: number;
  disabled?: boolean;
  zIndex?: number;
}

export const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  position = 'bottom',
  showArrow = true,
  open: controlledOpen,
  onOpen,
  onClose,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  className,
  contentClassName = 'p-4',
  arrowClassName,
  offset = 8,
  disabled = false,
  zIndex = 1000,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [arrowCoords, setArrowCoords] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);

  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : isOpen;

  // Calculate popover and arrow positions
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

    let top = 0,
      left = 0,
      arrowTop = 0,
      arrowLeft = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top + scrollTop - popoverRect.height - offset;
        left =
          triggerRect.left +
          scrollLeft +
          (triggerRect.width - popoverRect.width) / 2;
        arrowTop = triggerRect.top + scrollTop - 16;
        arrowLeft = triggerRect.left + scrollLeft + triggerRect.width / 2 - 8;
        break;
      case 'bottom':
        top = triggerRect.bottom + scrollTop + offset;
        left =
          triggerRect.left +
          scrollLeft +
          (triggerRect.width - popoverRect.width) / 2;
        arrowTop = triggerRect.bottom + scrollTop;
        arrowLeft = triggerRect.left + scrollLeft + triggerRect.width / 2 - 8;
        break;
      case 'left':
        top =
          triggerRect.top +
          scrollTop +
          (triggerRect.height - popoverRect.height) / 2;
        left = triggerRect.left + scrollLeft - popoverRect.width - offset;
        arrowTop = triggerRect.top + scrollTop + triggerRect.height / 2 - 8;
        arrowLeft = triggerRect.left + scrollLeft - 16;
        break;
      case 'right':
        top =
          triggerRect.top +
          scrollTop +
          (triggerRect.height - popoverRect.height) / 2;
        left = triggerRect.right + scrollLeft + offset;
        arrowTop = triggerRect.top + scrollTop + triggerRect.height / 2 - 8;
        arrowLeft = triggerRect.right + scrollLeft;
        break;
    }

    // Ensure popover stays within viewport bounds
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    if (left < 0) left = 8;
    if (left + popoverRect.width > viewportWidth)
      left = viewportWidth - popoverRect.width - 8;
    if (top < 0) top = 8;
    if (top + popoverRect.height > viewportHeight)
      top = viewportHeight - popoverRect.height - 8;

    setPopoverCoords({ top, left });
    setArrowCoords(showArrow ? { top: arrowTop, left: arrowLeft } : null);
    setIsPositioned(true);
  }, [position, offset, showArrow]);

  // Handle open/close logic
  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (isControlled) {
      if (open) onClose?.();
      else onOpen?.();
    } else {
      setIsOpen(prev => {
        if (!prev) onOpen?.();
        else onClose?.();
        return !prev;
      });
    }
  }, [disabled, isControlled, open, onOpen, onClose]);

  const handleClose = useCallback(() => {
    if (isControlled) onClose?.();
    else {
      setIsOpen(false);
      onClose?.();
    }
  }, [isControlled, onClose]);

  // Close on outside click
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (!closeOnOutsideClick) return;
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      )
        return;
      handleClose();
    },
    [closeOnOutsideClick, handleClose]
  );

  // Close on escape key
  const handleEscapeKey = useCallback(
    (event: KeyboardEvent) => {
      if (!closeOnEscape || event.key !== 'Escape') return;
      handleClose();
    },
    [closeOnEscape, handleClose]
  );

  // Position calculation and event listeners
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Position calculation pattern: calculatePosition is a stable callback that sets state for dynamic positioning
      calculatePosition();
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleEscapeKey);
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('keydown', handleEscapeKey);
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    } else {
      setIsPositioned(false);
    }
  }, [open, calculatePosition, handleOutsideClick, handleEscapeKey]);

  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(calculatePosition, 0);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [open, calculatePosition]);

  // Arrow classes helper
  const getArrowClasses = () =>
    cn(
      'absolute w-4 h-4 bg-chariot-background-primary border border-chariot-stroke',
      arrowClassName,
      position === 'bottom' && 'border-b-0 border-r-0',
      position === 'left' && 'border-l-0 border-b-0',
      position === 'right' && 'border-r-0 border-t-0',
      position === 'top' && 'border-l-0 border-t-0'
    );

  // Popover content
  const popoverContent = open ? (
    <div
      ref={popoverRef}
      className={cn(
        'text-chariot-text-primary bg-chariot-background-primary border-chariot-stroke fixed overflow-hidden rounded-lg border shadow-lg',
        'max-w-[400px] min-w-[200px] transition-opacity duration-150',
        contentClassName
      )}
      style={{
        top: popoverCoords.top,
        left: popoverCoords.left,
        zIndex,
        opacity: isPositioned ? 1 : 0,
      }}
      data-testid="popover-content"
    >
      {children}
    </div>
  ) : null;

  // Render
  return (
    <div className={className}>
      <div
        ref={triggerRef}
        onClick={handleToggle}
        className={cn(
          'cursor-pointer',
          disabled && 'cursor-not-allowed opacity-50'
        )}
        data-testid="popover-trigger"
      >
        {trigger}
      </div>
      {open &&
        arrowCoords &&
        createPortal(
          <div
            className={getArrowClasses()}
            style={{
              position: 'absolute',
              top: arrowCoords.top,
              left: arrowCoords.left,
              transform: 'rotate(45deg)',
              zIndex: zIndex + 1,
            }}
          />,
          document.body
        )}
      {createPortal(popoverContent, document.body)}
    </div>
  );
};
